import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import { toast } from '@/stores/useToastStore'
import { useUpdatePlanHeader } from '@/hooks/useAssortmentPlans'
import { getConfigurableColumns } from './columnConfiguration'
import type { DagColumnMeta } from '@/types/dag'

const MAX_PRIMARY_ATTRIBUTES = 6

interface ColumnsConfigurationDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  columns: DagColumnMeta[]
  primaryAttributes: string[]
  planId: string
}

export function ColumnsConfigurationDrawer({
  open,
  onOpenChange,
  columns,
  primaryAttributes,
  planId,
}: ColumnsConfigurationDrawerProps) {
  const updatePlanHeader = useUpdatePlanHeader(planId)

  const [sessionChanges, setSessionChanges] = useState<
    Map<string, 'promoted' | 'demoted'>
  >(new Map())
  const [hoveredColumnId, setHoveredColumnId] = useState<string | null>(null)
  const [localPrimaryIds, setLocalPrimaryIds] =
    useState<string[]>(primaryAttributes)

  // Reset state only when drawer transitions from closed to open
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSessionChanges(new Map())
      setLocalPrimaryIds(primaryAttributes)
    }
    prevOpenRef.current = open
  }, [open, primaryAttributes])

  const configurableColumns = useMemo(
    () => getConfigurableColumns(columns),
    [columns]
  )

  const primarySet = useMemo(() => new Set(localPrimaryIds), [localPrimaryIds])
  const isAtLimit = localPrimaryIds.length >= MAX_PRIMARY_ATTRIBUTES

  const handleToggle = useCallback(
    (columnId: string) => {
      if (
        !primarySet.has(columnId) &&
        localPrimaryIds.length >= MAX_PRIMARY_ATTRIBUTES
      )
        return
      const isPrimary = primarySet.has(columnId)
      const previous = localPrimaryIds

      const next = isPrimary
        ? localPrimaryIds.filter(id => id !== columnId)
        : Array.from(new Set([...localPrimaryIds, columnId]))

      // Optimistic update
      setLocalPrimaryIds(next)

      // Track session changes
      setSessionChanges(prev => {
        const updated = new Map(prev)
        const existing = updated.get(columnId)

        if (
          (isPrimary && existing === 'promoted') ||
          (!isPrimary && existing === 'demoted')
        ) {
          // Reverting a previous change
          updated.delete(columnId)
        } else {
          updated.set(columnId, isPrimary ? 'demoted' : 'promoted')
        }
        return updated
      })

      updatePlanHeader.mutate(
        { primary_attribute_ids: next },
        {
          onError: () => {
            setLocalPrimaryIds(previous)
            // Rollback session change
            setSessionChanges(prev => {
              const updated = new Map(prev)
              const existing = updated.get(columnId)
              if (existing) {
                updated.delete(columnId)
              } else {
                updated.set(columnId, isPrimary ? 'promoted' : 'demoted')
              }
              return updated
            })
            toast.error('Failed to update column configuration')
          },
        }
      )
    },
    [primarySet, localPrimaryIds, updatePlanHeader]
  )

  const getHoverLabel = useCallback(
    (columnId: string) => {
      const isPrimary = primarySet.has(columnId)
      const sessionChange = sessionChanges.get(columnId)

      if (isPrimary && sessionChange === 'promoted')
        return 'Revert to Secondary'
      if (!isPrimary && sessionChange === 'demoted') return 'Revert to Primary'
      if (isPrimary) return 'Make Secondary'
      if (isAtLimit) return 'Limit reached'
      return 'Make Primary'
    },
    [primarySet, sessionChanges, isAtLimit]
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-[600px] sm:max-w-[600px] flex flex-col p-0 gap-0"
      >
        <SheetHeader className="px-6 py-7 border-b border-[#e5e7eb] flex-row items-center justify-between space-y-0">
          <SheetTitle className="text-xl font-semibold text-[#101828]">
            Columns Configuration
          </SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 cursor-pointer"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </SheetHeader>

        {isAtLimit && (
          <div className="mx-6 mt-6 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            You have reached the maximum of {MAX_PRIMARY_ATTRIBUTES} primary
            attributes. Demote an existing primary attribute to add a new one.
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 pt-6">
          <div className="flex flex-col gap-1.5">
            {configurableColumns.map(column => {
              const isPrimary = primarySet.has(column.id)
              const isHovered = hoveredColumnId === column.id
              const hasChanged = sessionChanges.has(column.id)
              const isDisabled = !isPrimary && isAtLimit

              return (
                <div
                  key={column.id}
                  className={`flex items-center justify-between px-2.5 py-2 border border-[#d6dae1] rounded transition-colors ${
                    isDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  } ${
                    isHovered && !isDisabled
                      ? 'bg-red-50'
                      : hasChanged
                        ? 'bg-[#f8f8f8]'
                        : 'bg-white'
                  }`}
                  onMouseEnter={() => setHoveredColumnId(column.id)}
                  onMouseLeave={() => setHoveredColumnId(null)}
                  onClick={() => !isDisabled && handleToggle(column.id)}
                >
                  <span
                    className={`text-sm text-[#556374] ${
                      isHovered ? 'text-destructive' : ''
                    } ${hasChanged ? 'font-semibold' : ''}`}
                  >
                    {column.label}
                  </span>
                  <span
                    className={`text-xs text-[#556374] ${
                      isHovered ? 'text-destructive font-medium' : ''
                    } ${hasChanged ? 'font-semibold' : ''}`}
                  >
                    {isHovered
                      ? getHoverLabel(column.id)
                      : isPrimary
                        ? 'Primary'
                        : 'Secondary'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <SheetFooter className="px-6 pt-6 pb-6 border-t border-[#d6dae1] bg-[#f8f8f8]">
          <Button
            variant="outline"
            className="w-full cursor-pointer"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
